<!-- @format -->
<template>
    <div class="widget document-template-select-widget">
        <div class="widget-header">
            <h3>Choose Templates to add</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <p>Select the template</p>
            <selector-table
                v-model="selectedItems"
                item-container-title="Selected Templates"
                :is-initializing="isLoading"
                :items="availableItems"
                :selected-items="selectedItems"
                :selected-item-cell-config="selectedItemCellConfig"
                :table-config="tableConfig"
                @removed-item="handleRemovedItem"
                @remove-all="handleRemoveAll"
                @selected-item="handleSelectedItem"
                @query="handleQuery"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Close</app-button>
            </div>
            <div class="action-container">
                <app-button :is-loading="isLoading" @click="add"> Add </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { DocumentTemplateBoardServiceMixin } from '@/mixins/board/document-template-board-service-mixin';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SelectorTable = () => import('@/components/selector-table/selector-table');

export default {
    name: 'document-template-select-widget',

    components: {
        'app-button': AppButton,
        'selector-table': SelectorTable,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [DocumentTemplateBoardServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            items: [],
            selectedItems: [],
            selectedItemCellConfig: {
                type: 'document-template-element',
            },

            tableConfig: [
                {
                    cellWidth: '300px',
                    id: 1,
                    label: 'Name',
                    type: 'document-template-element',
                    customHeaderStyles: {
                        'font-size': '12px',
                        justifyContent: 'center',
                        width: '345px',
                    },

                    customCellStyles: {
                        'align-items': 'center',
                        'border-right': '2px solid #E9E9EF',
                        display: 'flex',
                        height: '100%',
                        padding: '0 10px',
                    },
                },
                {
                    cellWidth: '250px',
                    id: 2,
                    label: 'Category',
                    type: 'document-template-category',
                    customHeaderStyles: {
                        'font-size': '12px',
                        justifyContent: 'center',
                        width: '265px',
                    },

                    customCellStyles: {
                        padding: '0 24px',
                    },
                },
            ],
        };
    },

    computed: {
        availableItems() {
            return this.items.reduce((acc, item) => {
                if (
                    !(
                        this.selectedItems.length > 0 &&
                        this.selectedItems.some(({ id }) => id === item.id)
                    )
                ) {
                    acc.push(item);
                }
                return acc;
            }, []);
        },

        initialSelectedItems() {
            return this.widget.initialSelectedIds;
        },
    },

    created() {
        const initializeData = () => {
            return new Promise((resolve, reject) =>
                this.getData()
                    .then(() => {
                        if (this.initialSelectedItems.length) {
                            this.setInitialSelected(this.initialSelectedItems);
                        }
                        resolve();
                    })
                    .catch(reject),
            );
        };

        this.withLoadingBoundary(initializeData);
    },

    methods: {
        asTableItems(items) {
            return items.map((item) => ({
                id: item.id,
                name: item.template.name,
                thumbnailImageUrl: item.template.previewImageUrl || null,
                categories: item.categories,
            }));
        },

        handleQuery(options) {
            this.withLoadingBoundary(() => this.getData(options));
        },

        getData(options = {}) {
            this.items = [];
            return new Promise((resolve, reject) => {
                options.select = 'Id,Name,Categories,DocumentTemplateUrls';

                this.dispatchGetDocumentTemplates({ options })
                    .then(({ items, count }) => {
                        this.items = this.asTableItems(items);
                        this.totalItems = count;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your document templates.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        setInitialSelected() {
            this.selectedItems = this.items.filter(({ id }) =>
                this.initialSelectedItems.includes(id),
            );
        },

        handleRemoveAll() {
            this.selectedItems.splice(0, this.selectedItems.length);
        },

        handleRemovedItem(item) {
            this.selectedItems = this.selectedItems.filter(({ id }) => id !== item.id);
        },

        handleSelectedItem(item) {
            if (this.selectedItems.every(({ id }) => id !== item.id)) {
                this.selectedItems.push(item);
            }
        },

        add() {
            EventBus.$emit(
                'document-template:select',
                this.selectedItems.map((item) => item.id),
            );
            this.closeWidget();
        },

        withLoadingBoundary(promise) {
            this.isLoading = true;
            promise().finally(() => {
                this.isLoading = false;
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.document-template-select-widget .content-wrapper {
    padding: 17px !important;
}

.document-template-select-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    border: 2px solid #cac6cb;
    color: #382153;
}

.document-template-select-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.document-template-select-widget .content-wrapper .items-table .items-table-body-container {
    margin: 12px 0 0;
}

.document-template-select-widget .content-wrapper .items-table .items-table-body-cells-container {
    gap: 0px;
}

.document-template-select-widget .items-table .items-table-body-cells-container .body-cell {
    max-width: 100%;
}
</style>
